/**
 * Author: Romain Hapi Sr.
 * Version: 2.0
 * Date: 2026-09-10
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Page de contact du site Tchad.
 *
 * Le formulaire poste vers l'API du Tchad. Comme au UK, le message est
 * enregistre meme si le courriel ne part pas ; l'echec d'envoi n'est donc pas
 * remonte au visiteur comme une erreur de saisie.
 */
import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { PageHero } from '../components/Section';
import { useReveal } from '../hooks/useReveal';
import SEO from '../../components/SEO';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../components/ui/sonner';
import { SHELL, SECTION, BTN_PRIMARY, CARD } from '../theme';

const META = {
  fr: {
    title: 'Contact — Prenim Tchad',
    description:
      "Bureaux a N'Djamena et a Leeds. Ecrivez-nous ou appelez-nous pour discuter de votre projet.",
  },
  en: {
    title: 'Contact — Prenim Tchad',
    description: "Offices in N'Djamena and Leeds. Write or call us to discuss your project.",
  },
  ar: { title: 'اتصل بنا — Prenim Tchad', description: 'مكاتبنا في نجامينا وليدز.' },
};

const EYEBROW = { fr: 'Parlons-en', en: 'Get in touch', ar: 'تواصل معنا' };
const LABELS = {
  fr: { address: 'Adresse', phone: 'Téléphone', email: 'Courriel', map: 'Nous situer' },
  en: { address: 'Address', phone: 'Phone', email: 'Email', map: 'Find us' },
  ar: { address: 'العنوان', phone: 'الهاتف', email: 'البريد', map: 'موقعنا' },
};

export const Contact = () => {
  const { t, language } = useLanguage();
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });

  const info = useReveal();

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Le bouton se verrouille pendant l'envoi : sans cela, un double clic
    // expediait deux fois le meme message.
    if (sending) return;
    setSending(true);
    try {
      const { submitContact } = await import('../services/api');
      const response = await submitContact(formData);
      toast.success(response.message || 'Message envoyé! Nous vous contacterons bientôt.');
      setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const meta = META[language] || META.fr;
  const labels = LABELS[language] || LABELS.fr;

  const offices = [
    {
      name: t.contact.chad,
      address: "694 Avenue de Bezo, N'Djamena",
      phones: ['+235 85 20 67 61', '+235 90 17 66 13'],
      email: 'hello@prenim.co.uk',
    },
    {
      name: t.contact.uk,
      address: '30 Button Hill, Leeds, LS7 3DA',
      phones: ['+44 113 880 5181'],
      email: 'hello@prenim.co.uk',
    },
  ];

  const fieldClass = 'mt-2 w-full';
  const labelClass = 'block text-sm font-semibold text-slate-700';

  return (
    <main data-testid="tchad-contact-page">
      <SEO title={meta.title} description={meta.description} lang={language} />

      <PageHero
        eyebrow={EYEBROW[language] || EYEBROW.fr}
        title={t.contact.title}
        lead={t.contact.subtitle}
      />

      <section className={`${SECTION} bg-slate-50`}>
        <div className={SHELL}>
          <div className="grid gap-10 lg:grid-cols-12">
            {/* Formulaire. Chaque libelle est relie a son champ par `htmlFor`
                et `id` : sans cette paire, un lecteur d'ecran annonce un champ
                sans nom, et cliquer le libelle ne place pas le curseur. */}
            <div className="lg:col-span-7">
              <div className="rounded-lg border border-slate-200 bg-white p-8 lg:p-10">
                <h2 className="text-2xl font-bold tracking-tight text-slate-950 lg:text-3xl">
                  {t.contact.send}
                </h2>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6" noValidate={false}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-first-name" className={labelClass}>
                        {t.contact.first_name} *
                      </label>
                      <Input
                        id="contact-first-name"
                        type="text"
                        name="firstName"
                        autoComplete="given-name"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className={fieldClass}
                        data-testid="tchad-contact-first-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-last-name" className={labelClass}>
                        {t.contact.last_name} *
                      </label>
                      <Input
                        id="contact-last-name"
                        type="text"
                        name="lastName"
                        autoComplete="family-name"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className={fieldClass}
                        data-testid="tchad-contact-last-name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-email" className={labelClass}>
                      {t.contact.email} *
                    </label>
                    <Input
                      id="contact-email"
                      type="email"
                      name="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={fieldClass}
                      data-testid="tchad-contact-email"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-phone" className={labelClass}>
                      {t.contact.phone}
                    </label>
                    <Input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className={fieldClass}
                      data-testid="tchad-contact-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className={labelClass}>
                      {t.contact.message} *
                    </label>
                    <Textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={7}
                      className={fieldClass}
                      data-testid="tchad-contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="tchad-contact-submit"
                    className={`${BTN_PRIMARY} w-full justify-center disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    <Send className="h-4 w-4 rtl-flip" />
                    {t.contact.send}
                  </button>
                </form>
              </div>
            </div>

            <div ref={info.ref} className={`${info.className} space-y-6 lg:col-span-5`}>
              {offices.map((office) => (
                <div key={office.name} className={`${CARD} p-8`}>
                  <h2 className="text-xs font-semibold uppercase tracking-widest text-amber-600">
                    {office.name}
                  </h2>
                  <div className="tchad-rule mt-3" />

                  <dl className="mt-6 space-y-5 text-sm">
                    <div className="flex items-start gap-3">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <div>
                        <dt className="no-justify font-semibold text-slate-950">
                          {labels.address}
                        </dt>
                        <dd className="no-justify mt-1 text-slate-600">{office.address}</dd>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Phone className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <div>
                        <dt className="no-justify font-semibold text-slate-950">{labels.phone}</dt>
                        {office.phones.map((phone) => (
                          <dd key={phone} className="mt-1">
                            <a
                              href={`tel:${phone.replace(/\s/g, '')}`}
                              className="text-slate-600 transition-colors hover:text-amber-600"
                            >
                              {phone}
                            </a>
                          </dd>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Mail className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <div>
                        <dt className="no-justify font-semibold text-slate-950">{labels.email}</dt>
                        <dd className="mt-1">
                          <a
                            href={`mailto:${office.email}`}
                            className="text-slate-600 transition-colors hover:text-amber-600"
                          >
                            {office.email}
                          </a>
                        </dd>
                      </div>
                    </div>
                  </dl>
                </div>
              ))}

              <div className="overflow-hidden rounded-lg border border-slate-200">
                <iframe
                  title={`${labels.map} — N'Djamena`}
                  width="100%"
                  height="280"
                  style={{ border: 0, display: 'block' }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://maps.google.com/maps?q=694+Avenue+de+Bezo,+N%27Djamena,+Tchad&output=embed&hl=fr&z=15"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
